/**
 * Hooks de leitura para o dashboard "Observabilidade IA" da Base de Conhecimento.
 * Todas as leituras são feitas via RPCs `kb_ai_obs_*` (SECURITY DEFINER, root_admin only).
 *
 * Estrutura padronizada:
 *   queryKey: ['admin','kb-ai-obs', <slice>, ...args]
 *   refetchInterval: 30s (dados analíticos, não realtime)
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

const REFRESH_MS = 30_000;

/**
 * Códigos Postgres que indicam falha estrutural/permissão: não faz sentido
 * fazer retry automático (loop de rede). Deixamos o erro subir para a UI.
 *   42501 -> insufficient_privilege (guard root_admin)
 *   42703 -> undefined_column
 *   42804 -> datatype_mismatch (ex.: COALESCE com enum)
 *   42P01 -> undefined_table
 */
const NON_RETRYABLE_PG_CODES = new Set(["42501", "42703", "42804", "42P01"]);

/**
 * Callback padrão para React Query: aborta retry em erros permanentes.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  if (code && NON_RETRYABLE_PG_CODES.has(code)) return false;
  return failureCount < 2;
}

// ---------------------------------------------------------------------------
// Tipos compartilhados
// ---------------------------------------------------------------------------
export interface KbAiObsFilters {
  from: string; // ISO
  to: string;   // ISO
  tenantIds?: string[] | null;
  purposes?: string[] | null;
}

export interface KbAiObsKpisPayload {
  current: {
    calls: number;
    success: number;
    errors: number;
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    cost_usd: number;
    latency_avg_ms: number;
    latency_p95_ms: number;
  };
  previous: {
    calls: number;
    total_tokens: number;
    cost_usd: number;
  };
}

export interface KbAiObsTimeseriesRow {
  bucket_day: string;
  purpose: string;
  calls: number;
  total_tokens: number;
  cost_usd: number;
  latency_avg: number;
}

export interface KbAiObsTenantRow {
  tenant_id: string;
  tenant_name: string;
  calls: number;
  errors: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  latency_avg_ms: number;
  latency_p95_ms: number;
  last_call_at: string;
  total_rows: number;
}

export interface KbAiObsModelRow {
  model_id: string;
  model_slug: string;
  provider_name: string;
  calls: number;
  errors: number;
  total_tokens: number;
  cost_usd: number;
  latency_avg_ms: number;
}

export interface KbAiObsPurposeRow {
  purpose: string;
  calls: number;
  total_tokens: number;
  cost_usd: number;
}

export interface KbAiObsCallRow {
  id: string;
  created_at: string;
  tenant_id: string | null;
  tenant_name: string;
  purpose: string;
  operation: string | null;
  model_slug: string;
  provider_name: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  latency_ms: number;
  status: string;
  error_code: string;
  page_id: string | null;
  request_id: string | null;
  total_rows: number;
}

export interface KbAiObsJobsHealth {
  embedding: { pending: number; processing: number; failed: number; done_24h: number };
  transcription: { pending: number; processing: number; failed: number; done_24h: number };
  dlq_total: number;
}

export interface KbAiObsSearchStats {
  total_searches: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
  avg_results: number;
  zero_result_pct: number;
  by_type: Record<string, number> | null;
}

// ---------------------------------------------------------------------------
// KPIs
// ---------------------------------------------------------------------------
export function useKbAiObsKpis(filters: KbAiObsFilters) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "kpis", filters],
    queryFn: async (): Promise<KbAiObsKpisPayload> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_kpis" as any, {
        p_from: filters.from,
        p_to: filters.to,
        p_tenant_ids: filters.tenantIds ?? null,
        p_purposes: filters.purposes ?? null,
      });
      if (error) {
        logger.error("kb-ai-obs", "kpis error", error);
        throw error;
      }
      return data as KbAiObsKpisPayload;
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Timeseries
// ---------------------------------------------------------------------------
export function useKbAiObsTimeseries(filters: KbAiObsFilters) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "timeseries", filters],
    queryFn: async (): Promise<KbAiObsTimeseriesRow[]> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_timeseries" as any, {
        p_from: filters.from,
        p_to: filters.to,
        p_tenant_ids: filters.tenantIds ?? null,
        p_purposes: filters.purposes ?? null,
      });
      if (error) throw error;
      return (data ?? []) as KbAiObsTimeseriesRow[];
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Por tenant
// ---------------------------------------------------------------------------
export function useKbAiObsByTenant(
  from: string,
  to: string,
  purposes: string[] | null,
  limit = 50,
  offset = 0,
) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "by-tenant", { from, to, purposes, limit, offset }],
    queryFn: async (): Promise<KbAiObsTenantRow[]> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_by_tenant" as any, {
        p_from: from,
        p_to: to,
        p_purposes: purposes,
        p_limit: limit,
        p_offset: offset,
      });
      if (error) throw error;
      return (data ?? []) as KbAiObsTenantRow[];
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Por modelo
// ---------------------------------------------------------------------------
export function useKbAiObsByModel(filters: KbAiObsFilters) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "by-model", filters],
    queryFn: async (): Promise<KbAiObsModelRow[]> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_by_model" as any, {
        p_from: filters.from,
        p_to: filters.to,
        p_tenant_ids: filters.tenantIds ?? null,
        p_purposes: filters.purposes ?? null,
      });
      if (error) throw error;
      return (data ?? []) as KbAiObsModelRow[];
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Por purpose (distribuição)
// ---------------------------------------------------------------------------
export function useKbAiObsByPurpose(
  from: string,
  to: string,
  tenantIds: string[] | null,
) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "by-purpose", { from, to, tenantIds }],
    queryFn: async (): Promise<KbAiObsPurposeRow[]> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_by_purpose" as any, {
        p_from: from,
        p_to: to,
        p_tenant_ids: tenantIds,
      });
      if (error) throw error;
      return (data ?? []) as KbAiObsPurposeRow[];
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Chamadas recentes (paginado)
// ---------------------------------------------------------------------------
export interface KbAiObsRecentArgs {
  from: string;
  to: string;
  tenantIds?: string[] | null;
  purposes?: string[] | null;
  status?: string | null;
  search?: string | null;
  limit?: number;
  offset?: number;
}

export function useKbAiObsRecentCalls(args: KbAiObsRecentArgs) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "recent-calls", args],
    queryFn: async (): Promise<KbAiObsCallRow[]> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_recent_calls" as any, {
        p_from: args.from,
        p_to: args.to,
        p_tenant_ids: args.tenantIds ?? null,
        p_purposes: args.purposes ?? null,
        p_status: args.status ?? null,
        p_search: args.search ?? null,
        p_limit: args.limit ?? 50,
        p_offset: args.offset ?? 0,
      });
      if (error) throw error;
      return (data ?? []) as KbAiObsCallRow[];
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Detalhe de uma chamada
// ---------------------------------------------------------------------------
export function useKbAiObsCallDetail(callId: string | null) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "call-detail", callId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_ai_obs_call_detail" as any, {
        p_call_id: callId,
      });
      if (error) throw error;
      return data as Record<string, any>;
    },
    enabled: !!callId,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Saúde das filas
// ---------------------------------------------------------------------------
export function useKbAiObsJobsHealth(tenantIds: string[] | null) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "jobs-health", tenantIds],
    queryFn: async (): Promise<KbAiObsJobsHealth> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_jobs_health" as any, {
        p_tenant_ids: tenantIds,
      });
      if (error) throw error;
      return data as KbAiObsJobsHealth;
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}

// ---------------------------------------------------------------------------
// Estatísticas de busca
// ---------------------------------------------------------------------------
export function useKbAiObsSearchStats(
  from: string,
  to: string,
  tenantIds: string[] | null,
) {
  return useQuery({
    queryKey: ["admin", "kb-ai-obs", "search-stats", { from, to, tenantIds }],
    queryFn: async (): Promise<KbAiObsSearchStats> => {
      const { data, error } = await supabase.rpc("kb_ai_obs_search_stats" as any, {
        p_from: from,
        p_to: to,
        p_tenant_ids: tenantIds,
      });
      if (error) throw error;
      return data as KbAiObsSearchStats;
    },
    refetchInterval: REFRESH_MS,
    retry: shouldRetry,
  });
}
